import { useState, useEffect, useRef } from 'react';
import SectionReveal from './SectionReveal';
import DirectionsSection from './DirectionsSection';

import bg1 from '../assets/bg1.png';
import bg2 from '../assets/bg2.png';
import bg3 from '../assets/bg3.png';

const bgImages = [bg1, bg2, bg3];

export default function HeroSection({ t }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const sectionRef = useRef(null);

  /*
   * ==========================================
   * PRELOAD IMAGES
   * ==========================================
   */
  useEffect(() => {
    bgImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  /*
   * ==========================================
   * INTERSECTION OBSERVER
   *
   * Останавливаем анимацию,
   * когда Hero ушёл из экрана
   * ==========================================
   */
  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  /*
   * ==========================================
   * CHANGE BACKGROUND
   * ==========================================
   */
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        return (prevIndex + 1) % bgImages.length;
      });
    }, 7000);

    return () => {
      clearInterval(interval);
    };
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      id="home"
      className="
        relative
        min-h-screen
        flex
        items-center
        bg-[#0B3B6F]
        rounded-none
        overflow-hidden
        pt-20
        md:pt-24
      "
    >

      {/* =====================================
          BACKGROUND IMAGES
          ===================================== */}

      <div className="absolute rounded-none inset-0 overflow-hidden">

        {bgImages.map((img, idx) => {
          const isActive = idx === currentImageIndex;

          return (
            <img
              key={img}
              src={img}
              alt=""
              aria-hidden="true"
              draggable="false"
              className={`
                absolute
                inset-0
                w-full
                h-full
                object-cover
                object-center
                pointer-events-none
                select-none
                transition-opacity
                ease-in-out
                ${isActive
                  ? 'opacity-100 duration-1000 hero-zoom'
                  : 'opacity-0 duration-1000'
                }
              `}
            />
          );
        })}

      </div>

      {/* =====================================
          DARK OVERLAY
          ===================================== */}

      <div
        className="
          absolute
          inset-0
          pointer-events-none
          bg-gradient-to-r
          rounded-none
          from-black/45
          to-black/55
        "
      />

      {/* =====================================
          LIGHT EFFECT
          ===================================== */}

      <div
        className={`
          absolute
          top-1/3
          left-1/4
          -translate-x-1/2
          -translate-y-1/2
          w-[450px]
          h-[220px]
          rounded-full
          bg-[#0FA4E8]/10
          blur-[60px]
          pointer-events-none
          transition-opacity
          duration-1000
          ${isVisible
            ? 'opacity-100'
            : 'opacity-0'
          }
        `}
      />

      {/* =====================================
          CONTENT
          ===================================== */}

      <div
        className="
          relative
          z-10
          w-full
          px-3
          py-20
          md:px-12
          lg:px-20
        "
      >

        <div
          className="
            max-w-7xl
            mx-auto
            space-y-8
            md:space-y-10
          "
        >

          {/* =================================
              BADGE
              ================================= */}

          <SectionReveal
            animation="fade-up"
            delay={0}
          >
            <div
              className="
                inline-flex
                items-center
                gap-3
                px-2
                py-2
                bg-white/10
                backdrop-blur-md
                border
                border-white/20
              "
            >

              <span
                className="
                  w-2
                  h-2
                  bg-[#0FA4E8]
                "
              />

              <span
                className="
                  text-sm
                  font-medium
                  tracking-[0.2em]
                  text-cyan-100
                  uppercase
                "
              >
                {t.heroBadge}
              </span>

            </div>
          </SectionReveal>


          {/* =================================
              TITLE
              ================================= */}

          <SectionReveal
            animation="fade-up"
            delay={150}
          >
            <h1
              className="
                text-3xl
                sm:text-4xl
                md:text-6xl
                lg:text-7xl
                font-black
                tracking-tight
                leading-[1.1]
              "
            >

              <span className="text-white">
                {t.heroTitle}
              </span>

              {' '}

              {t.heroTitle2 && (
                <span className="text-[#0FA4E8]">
                  {t.heroTitle2}
                </span>
              )}

            </h1>
          </SectionReveal>


          {/* =================================
              TEXT
              ================================= */}

          <SectionReveal
            animation="fade-up"
            delay={300}
          >
            <div
              className="
                p-3
                max-w-2xl
              "
            >

              <p
                className="
                  text-md
                  md:text-lg
                  font-light
                  leading-relaxed
                  text-slate-300
                "
              >
                {t.heroText}
              </p>

            </div>
          </SectionReveal>


          {/* =================================
              BUTTONS
              ================================= */}

          <SectionReveal
            animation="fade-up"
            delay={450}
          >

            <div
              className="
                flex
                flex-wrap
                gap-4
              "
            >

              {/* BUTTON 1 */}

             <a
  href="#call"
  className="
    px-8
    py-4
    bg-[#0B3B6F]
    text-white
    font-extrabold
    uppercase
    tracking-wider
    text-sm
    hover:bg-[#135ba8]
    transition-colors
    shadow-lg
    flex
    items-center
    gap-3
    group
  "
>
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className="flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
  {t.btnSolutions}
</a>


              {/* BUTTON 2

              <a
                href="#solutions-page"
                className="
                  px-8
                  py-4
                  border-2
                  border-white/40
                  bg-white/10
                  text-white
                  font-extrabold
                  uppercase
                  tracking-wider
                  text-sm
                  hover:bg-white
                  hover:text-[#0B3B6F]
                  hover:border-white
                  transition-colors
                "
              >
                {t.btnContact}
              </a> */}

            </div>

          </SectionReveal>


          {/* =================================
              DIRECTIONS
              ================================= */}

          <DirectionsSection t={t} />

        </div>

      </div>

    </section>
  );
}